import { randomUniqueId } from "$routes/_lib/random-id";
import {
  Box,
  Button,
  Drawer,
  MenuItem,
  Select,
  styled,
  TextField,
  Typography,
} from "@material-ui/core";
import { useState } from "react";
import type {
  EpocusReferral,
  EpocusReferralUrgency,
} from "../../../../../global-providers/rxdb-provider/collections/epocus-referral";
import { useRxCollectionEpocusReferral } from "../../../../../global-providers/rxdb-provider/collections/epocus-referral";
import {
  EPOCUS_BORDER,
  EPOCUS_ICON_ACTIVE,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../../../_lib/epocus-palette";
import { useDemoViewer } from "../../../_lib/use-demo-viewer";
import { HARDCODED_RECEIVERS } from "../../../referrals/_lib/hardcoded-receivers";

const SheetContent = styled(Box)({
  padding: "24px 16px 32px",
  display: "flex",
  flexDirection: "column",
  gap: 16,
  backgroundColor: EPOCUS_WHITE,
  borderRadius: "16px 16px 0 0",
  maxWidth: 680,
  margin: "0 auto",
  width: "100%",
});

const FieldLabel = styled(Typography)({
  fontSize: rem(11),
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: EPOCUS_TEXT_SECONDARY,
  marginBottom: 4,
});

const URGENCY_OPTIONS: {
  value: EpocusReferralUrgency;
  label: string;
  color: string;
}[] = [
  { value: "routine", label: "Routine", color: "#6b7280" },
  { value: "urgent", label: "Urgent", color: "#d97706" },
  { value: "emergency", label: "Emergency", color: "#dc2626" },
];

export const ReferPatientSheet: FC<{
  open: boolean;
  onClose: () => void;
  encounterId: string;
  defaultClinicalReason?: string;
}> = ({ open, onClose, encounterId, defaultClinicalReason = "" }) => {
  const viewerId = useDemoViewer();
  const referralCollection = useRxCollectionEpocusReferral();

  const [urgency, setUrgency] = useState<EpocusReferralUrgency>("routine");
  const [clinicalReason, setClinicalReason] = useState(defaultClinicalReason);
  const [receiverId, setReceiverId] = useState(
    HARDCODED_RECEIVERS[0]?.id ?? "",
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSend = async () => {
    if (!referralCollection || !clinicalReason.trim()) return;
    setError(null);
    setSubmitting(true);
    try {
      const now = new Date().toISOString();
      const referral: EpocusReferral = {
        id: randomUniqueId(),
        encounterId,
        examId: null,
        referringUserId: viewerId ?? "",
        receivingUserId: receiverId,
        urgency,
        clinicalReason: clinicalReason.trim(),
        status: "sent",
        createdAt: now,
        sentAt: now,
        acknowledgedAt: null,
        outcomeRecordedAt: null,
      };
      await referralCollection.insert(referral);
      onClose();
    } catch {
      setError("Failed to send referral. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        style: { backgroundColor: "transparent", boxShadow: "none" },
      }}
    >
      <SheetContent>
        {/* Handle bar */}
        <Box
          style={{
            width: 36,
            height: 4,
            backgroundColor: EPOCUS_BORDER,
            borderRadius: 2,
            alignSelf: "center",
            marginBottom: 4,
          }}
        />

        <Typography
          variant="h6"
          style={{
            fontWeight: 700,
            fontSize: rem(18),
            color: EPOCUS_TEXT_PRIMARY,
          }}
        >
          Refer Patient
        </Typography>

        {/* Urgency toggle */}
        <Box>
          <FieldLabel>Urgency</FieldLabel>
          <Box display="flex" style={{ gap: 8 }}>
            {URGENCY_OPTIONS.map((opt) => {
              const active = urgency === opt.value;
              return (
                <Button
                  key={opt.value}
                  onClick={() => setUrgency(opt.value)}
                  variant={active ? "contained" : "outlined"}
                  size="small"
                  style={{
                    flex: 1,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: rem(13),
                    borderColor: opt.color,
                    color: active ? EPOCUS_WHITE : opt.color,
                    backgroundColor: active ? opt.color : "transparent",
                    minHeight: 40,
                  }}
                  disableElevation
                >
                  {opt.label}
                </Button>
              );
            })}
          </Box>
        </Box>

        {/* Clinical reason */}
        <Box>
          <FieldLabel>Clinical Reason</FieldLabel>
          <TextField
            multiline
            rows={3}
            value={clinicalReason}
            onChange={(e) => setClinicalReason(e.target.value)}
            variant="outlined"
            placeholder="Describe the clinical reason for this referral…"
            fullWidth
            inputProps={{ style: { fontSize: rem(16) } }}
          />
        </Box>

        {/* Receiver */}
        <Box>
          <FieldLabel>Refer To</FieldLabel>
          <Select
            value={receiverId}
            onChange={(e) => setReceiverId(e.target.value as string)}
            variant="outlined"
            fullWidth
            style={{ fontSize: rem(16) }}
          >
            {HARDCODED_RECEIVERS.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.name} — {r.role}, {r.facility}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {error && (
          <Typography
            style={{ color: "#dc2626", fontSize: rem(13), fontWeight: 600 }}
          >
            {error}
          </Typography>
        )}

        <Box display="flex" style={{ gap: 8 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            style={{
              flex: 1,
              textTransform: "none",
              fontSize: rem(15),
              minHeight: 48,
              borderRadius: 10,
              borderColor: EPOCUS_BORDER,
              color: EPOCUS_TEXT_SECONDARY,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            variant="contained"
            disabled={submitting || !clinicalReason.trim()}
            style={{
              flex: 2,
              textTransform: "none",
              fontWeight: 700,
              fontSize: rem(15),
              minHeight: 48,
              borderRadius: 10,
              backgroundColor: EPOCUS_ICON_ACTIVE,
              color: EPOCUS_WHITE,
            }}
            disableElevation
          >
            {submitting ? "Sending…" : "Send Referral"}
          </Button>
        </Box>
      </SheetContent>
    </Drawer>
  );
};
